import { createSelector, createRootSelector } from '@mui/x-data-grid-pro/internals';
import type { GridAggregationPosition } from '@mui/x-data-grid-pro/internals';
import { gridRowTreeSelector } from '@mui/x-data-grid-pro';
import type { GridRowId } from '@mui/x-data-grid-pro';
import type { GridStatePremium } from '../../../models/gridStatePremium';

export const gridAggregationStateSelector = createRootSelector(
  (state: GridStatePremium) => state.aggregation,
);

/**
 * Get the aggregation model, containing the aggregation function of each column.
 * If a column is not in the model, it is not aggregated.
 * @category Aggregation
 */
export const gridAggregationModelSelector = createSelector(
  gridAggregationStateSelector,
  (aggregationState) => aggregationState.model,
);

/**
 * Get the aggregation results as a lookup.
 * @category Aggregation
 */
export const gridAggregationLookupSelector = createSelector(
  gridAggregationStateSelector,
  (aggregationState) => aggregationState.lookup,
);

export const gridCellAggregationResultSelector = createSelector(
  gridRowTreeSelector,
  gridAggregationLookupSelector,
  (rowTree, aggregationLookup, { id, field }: { id: GridRowId; field: string | undefined }) => {
    if (!aggregationLookup || field === undefined) {
      return null;
    }

    let cellAggregationPosition: GridAggregationPosition | null = null;
    const rowNode = rowTree[id];

    if (!rowNode) {
      return null;
    }

    if (rowNode.type === 'group') {
      cellAggregationPosition = 'inline';
    } else if (id.toString().startsWith('auto-generated-group-footer-')) {
      cellAggregationPosition = 'footer';
    }

    if (cellAggregationPosition == null) {
      return null;
    }

    // TODO: Add custom root id
    const groupId = cellAggregationPosition === 'inline' ? id : (rowNode.parent ?? '');

    const aggregationResult = aggregationLookup?.[groupId]?.[field];
    if (!aggregationResult || aggregationResult.position !== cellAggregationPosition) {
      return null;
    }

    return aggregationResult;
  },
);
